import React, { Suspense, lazy } from 'react';
import { Capacitor } from '@capacitor/core';
import TeacherPhoneFrame from './components/TeacherPhoneFrame';
import { BrowserRouter, Routes, Route, Navigate, NavLink, useNavigate, useLocation } from 'react-router-dom';
import PublicLayout from './pages/public/Layout';
import { Home, ClipboardCheck, GraduationCap, Bell, Calendar, Ribbon, Wallet, School, BookOpen, Clock, User, MessageSquare, Users, Settings, ChevronDown, ChevronRight, MoreHorizontal, Compass, Megaphone, BarChart2 } from 'lucide-react';
import { AuthProvider, useAuth } from './context/AuthContext';
import { PermissionProvider, usePermissions } from './context/PermissionContext';
import { COLORS, getAvatarColor, getInitials } from './constants/theme';
import client, { resolveUrl } from './api/client';
import { useSchoolProfile, logoCacheBust } from './hooks/useSchoolProfile';
import { SchoolBrandProvider, useSchoolBrand } from './context/SchoolBrandContext';
import TermsModal, { CURRENT_TERMS_VERSION } from './components/TermsModal';
import { useTranslation } from 'react-i18next';

const isNativeApp = Capacitor.isNativePlatform();

/* ── Public marketing pages ── */
const PublicHome        = lazy(() => import('./pages/public/Home'));
const PublicFeatures    = lazy(() => import('./pages/public/Features'));
const PublicPricing     = lazy(() => import('./pages/public/Pricing'));
const PublicAbout       = lazy(() => import('./pages/public/About'));
const PublicContact     = lazy(() => import('./pages/public/Contact'));
const PublicRequestDemo = lazy(() => import('./pages/public/RequestDemo'));
const PublicPrivacyPolicy = lazy(() => import('./pages/public/PrivacyPolicy'));

/* ── Super Admin Portal ── */
const SuperAdminPortal = lazy(() => import('./pages/superadmin/SuperAdminPortal'));

/* ── Owner Portal ── */
const OwnerLogin        = lazy(() => import('./pages/owner/OwnerLogin'));
const OwnerDashboard    = lazy(() => import('./pages/owner/OwnerDashboard'));
const OwnerBranchDetail = lazy(() => import('./pages/owner/OwnerBranchDetail'));

/* ── Impersonation Banner — shown in AdminLayout when SA is impersonating ── */
function ImpersonationBanner() {
  const data = (() => { try { return JSON.parse(localStorage.getItem('impersonating') || 'null'); } catch { return null; } })();
  if (!data) return null;
  const returnToSA = () => {
    localStorage.removeItem('impersonating');
    localStorage.removeItem('token');
    window.location.href = '/sa';
  };
  return (
    <div style={{
      position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999,
      background: 'linear-gradient(135deg, #7c3aed, #6366f1)',
      padding: '10px 20px',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#fff', fontSize: 13, fontWeight: 600 }}>
        <span style={{ fontSize: 16 }}>🔑</span>
        You are currently impersonating <strong style={{ marginLeft: 4 }}>{data.school_name}</strong> Admin.
      </div>
      <button onClick={returnToSA} style={{
        padding: '6px 16px', background: 'rgba(255,255,255,0.2)',
        border: '1px solid rgba(255,255,255,0.4)', borderRadius: 8,
        color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer',
      }}>
        ← Return to Super Admin
      </button>
    </div>
  );
}

/* ── Lazy-loaded Pages ── */
const LoginGateway   = lazy(() => import('./pages/LoginGateway'));
const Login          = lazy(() => import('./pages/Login'));
const ParentLogin    = lazy(() => import('./pages/ParentLogin'));
const StudentLogin   = lazy(() => import('./pages/StudentLogin'));
const ChangePassword = lazy(() => import('./pages/ChangePassword'));
const ChangePIN      = lazy(() => import('./pages/ChangePIN'));
const PayReturn      = lazy(() => import('./pages/PayReturn'));

/* Teacher */
const TeacherHome          = lazy(() => import('./pages/teacher/Home'));
const AttendanceSelectClass= lazy(() => import('./pages/teacher/AttendanceSelectClass'));
const AttendanceMarking    = lazy(() => import('./pages/teacher/AttendanceMarking'));
const GradesSelectClass    = lazy(() => import('./pages/teacher/GradesSelectClass'));
const GradesTable          = lazy(() => import('./pages/teacher/GradesTable'));
const TeacherAlerts        = lazy(() => import('./pages/teacher/Alerts'));
const TeacherProfile       = lazy(() => import('./pages/teacher/Profile'));
const ClassNotes           = lazy(() => import('./pages/teacher/ClassNotes'));
const ViewStudents         = lazy(() => import('./pages/teacher/ViewStudents'));
const StudentDetail        = lazy(() => import('./pages/teacher/StudentDetail'));
const ClassReport          = lazy(() => import('./pages/teacher/ClassReport'));
const MyTimetable          = lazy(() => import('./pages/teacher/MyTimetable'));
const TeacherMessages      = lazy(() => import('./pages/teacher/Messages'));
const TeacherHomework      = lazy(() => import('./pages/teacher/Homework'));
const CreateHomework       = lazy(() => import('./pages/teacher/CreateHomework'));
const TeacherSubmissions   = lazy(() => import('./pages/teacher/Submissions'));
const ClassDashboard          = lazy(() => import('./pages/teacher/ClassDashboard'));
const NotificationCenter      = lazy(() => import('./pages/teacher/NotificationCenter'));
const TeacherPayslip          = lazy(() => import('./pages/teacher/Payslip'));
const TeacherGradeRequests    = lazy(() => import('./pages/teacher/GradeRequests'));

/* Admin */
const AdminDashboard   = lazy(() => import('./pages/admin/Dashboard'));
const AdminStudents    = lazy(() => import('./pages/admin/Students'));
const AdminTeachers    = lazy(() => import('./pages/admin/Teachers'));
const AdminClasses     = lazy(() => import('./pages/admin/Classes'));
const AdminClassDetail = lazy(() => import('./pages/admin/ClassDetail'));
const AdminSubjects    = lazy(() => import('./pages/admin/Subjects'));
const AdminParents      = lazy(() => import('./pages/admin/Parents'));
const AdminParentDetail = lazy(() => import('./pages/admin/ParentDetail'));
const AdminParentSlip   = lazy(() => import('./pages/admin/ParentSlip'));
const AdminFees        = lazy(() => import('./pages/admin/Fees'));
const AdminFeeTypes    = lazy(() => import('./pages/admin/FeeTypes'));
const AdminReports     = lazy(() => import('./pages/admin/Reports'));
const TimetableBuilder = lazy(() => import('./pages/admin/TimetableBuilder'));
const GatewaySettings  = lazy(() => import('./pages/admin/GatewaySettings'));
const SettingsSchoolProfile = lazy(() => import('./pages/admin/settings/SchoolProfile'));
const SettingsTerms         = lazy(() => import('./pages/admin/settings/Terms'));
const SettingsUsersRoles    = lazy(() => import('./pages/admin/settings/UsersRoles'));
const SettingsPayroll       = lazy(() => import('./pages/admin/settings/PayrollSettings'));
const SettingsAcademic      = lazy(() => import('./pages/admin/settings/AcademicSettings'));
const AdminAnnouncements    = lazy(() => import('./pages/admin/Announcements'));
const AdminGuideCenter      = lazy(() => import('./pages/admin/GuideCenterPage'));
const AdminPayroll          = lazy(() => import('./pages/admin/Payroll'));
const AdminMessages         = lazy(() => import('./pages/admin/Messages'));
// Academic Year Management (new)
const AcademicYearsPage    = lazy(() => import('./pages/admin/academic/AcademicYearsPage'));
const PromotionWizard      = lazy(() => import('./pages/admin/academic/PromotionWizard'));
const ReportCardsPage      = lazy(() => import('./pages/admin/academic/ReportCardsPage'));
const StudentAcademicProfilePage  = lazy(() => import('./pages/admin/academic/StudentAcademicProfilePage'));
const StudentAcademicRecordsPage  = lazy(() => import('./pages/admin/academic/StudentAcademicRecordsPage'));
const GradeFlagsPage              = lazy(() => import('./pages/admin/academic/GradeFlagsPage'));

/* Parent */
const ParentHome       = lazy(() => import('./pages/parent/Home'));
const ParentAttendance = lazy(() => import('./pages/parent/Attendance'));
const ParentGrades     = lazy(() => import('./pages/parent/Grades'));
const ParentFees       = lazy(() => import('./pages/parent/Fees'));
const ParentHomework   = lazy(() => import('./pages/parent/Homework'));
const ParentMessages   = lazy(() => import('./pages/parent/Messages'));
const ParentProfile    = lazy(() => import('./pages/parent/Profile'));
const ParentTimetable       = lazy(() => import('./pages/parent/Timetable'));
const ParentAnnouncements   = lazy(() => import('./pages/parent/Announcements'));

/* Student */
const StudentHome          = lazy(() => import('./pages/student/Home'));
const StudentHomework      = lazy(() => import('./pages/student/Homework'));
const StudentGrades        = lazy(() => import('./pages/student/Grades'));
const StudentAttendance    = lazy(() => import('./pages/student/Attendance'));
const StudentTimetable     = lazy(() => import('./pages/student/Timetable'));
const StudentAnnouncements = lazy(() => import('./pages/student/Announcements'));
const StudentMessages      = lazy(() => import('./pages/student/Messages'));
const StudentProfile       = lazy(() => import('./pages/student/Profile'));

/* Admin — Student Slip */
const AdminStudentSlip     = lazy(() => import('./pages/admin/StudentSlip'));
const AdminTeacherSlip     = lazy(() => import('./pages/admin/TeacherSlip'));
const AdminStaffSlip       = lazy(() => import('./pages/admin/StaffSlip'));

/* Registrar Portal */
const RegistrarDashboard   = lazy(() => import('./pages/registrar/Dashboard'));
const RegistrarProfile     = lazy(() => import('./pages/registrar/Profile'));
const RegistrarReports     = lazy(() => import('./pages/registrar/Reports'));

/* ── Page loading fallback ── */
function PageLoader() {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100%', minHeight: 200, color: '#9CA3AF', fontSize: 14,
    }}>
      Loading…
    </div>
  );
}

/* ─── Academic Years Nav Item (collapsible) ── */
const ACADEMIC_SUB_ITEMS = [
  { to: '/admin/academic-years',              label: 'Academic Years',           perm: 'manage_settings'       },
  { to: '/admin/academic/promotion',          label: 'Promotion & Graduation',   perm: 'full_access'           },
  { to: '/admin/academics/student-records',   label: 'Student Academic Records', perm: 'view_academic_records' },
  { to: '/admin/academics/report-cards',      label: 'Report Cards',             perm: 'view_all_reports'      },
  { to: '/admin/academics/grade-flags',       label: 'Grade Change Requests',    perm: 'view_all_reports'      },
];

function AcademicNavItem({ location, navigate, gradesFlagCount = 0 }) {
  const { hasPermission } = usePermissions();
  const visibleItems = ACADEMIC_SUB_ITEMS.filter(item => hasPermission(item.perm));
  const isActive = location.pathname.startsWith('/admin/academic') || location.pathname.startsWith('/admin/academics');
  const [open, setOpen] = React.useState(isActive);
  React.useEffect(() => { if (isActive) setOpen(true); }, [isActive]);
  if (visibleItems.length === 0) return null;
  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width:'100%', background: isActive ? 'rgba(255,255,255,0.18)' : 'none', border:'none', cursor:'pointer', textAlign:'left', padding:'10px 12px', borderRadius:10, color: isActive ? '#fff' : 'rgba(255,255,255,0.6)', fontSize:14, fontWeight: isActive ? 600 : 500, display:'flex', alignItems:'center', gap:12, transition:'all .12s' }}
        onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}}
        onMouseLeave={e => { e.currentTarget.style.background = isActive ? 'rgba(255,255,255,0.18)' : 'none'; e.currentTarget.style.color = isActive ? '#fff' : 'rgba(255,255,255,0.6)'; }}
      >
        <Calendar size={18} />
        <span style={{ flex:1 }}>Academics</span>
        {gradesFlagCount > 0 && (
          <span style={{ background: '#EF4444', color: '#fff', borderRadius: 10, fontSize: 9, fontWeight: 800, padding: '1px 6px', lineHeight: '14px', minWidth: 14, textAlign: 'center' }}>
            {gradesFlagCount > 99 ? '99+' : gradesFlagCount}
          </span>
        )}
        {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
      </button>
      {open && (
        <div style={{ paddingLeft:16, marginTop:2, display:'flex', flexDirection:'column', gap:1 }}>
          {visibleItems.map(({ to, label }) => {
            const isSubActive = location.pathname === to;
            const isGradeFlags = to === '/admin/academics/grade-flags';
            return (
              <button key={to} onClick={() => navigate(to)}
                style={{ width:'100%', background: isSubActive ? 'rgba(255,255,255,0.15)' : 'none', border:'none', cursor:'pointer', textAlign:'left', padding:'8px 12px', borderRadius:8, color: isSubActive ? '#fff' : 'rgba(255,255,255,0.55)', fontSize:13, fontWeight: isSubActive ? 600 : 400, display:'flex', alignItems:'center', gap:8, transition:'all .12s' }}
                onMouseEnter={e => { if (!isSubActive) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = isSubActive ? 'rgba(255,255,255,0.15)' : 'none'; e.currentTarget.style.color = isSubActive ? '#fff' : 'rgba(255,255,255,0.55)'; }}
              >
                <span style={{ width:4, height:4, borderRadius:2, background: isSubActive ? '#fff' : 'rgba(255,255,255,0.3)', flexShrink:0 }} />
                <span style={{ flex:1 }}>{label}</span>
                {isGradeFlags && gradesFlagCount > 0 && (
                  <span style={{ background: '#EF4444', color: '#fff', borderRadius: 10, fontSize: 9, fontWeight: 800, padding: '1px 6px', lineHeight: '14px', minWidth: 14, textAlign: 'center' }}>
                    {gradesFlagCount > 99 ? '99+' : gradesFlagCount}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Settings Nav Item (collapsible) ── */
const SETTINGS_SUB_ITEMS = [
  { to: '/admin/settings/school-profile', label: 'School Profile',    perm: 'manage_settings' },
  { to: '/admin/settings/users-roles',    label: 'Users & Roles',     perm: 'manage_settings' },
  { to: '/admin/settings/academic',       label: 'Academic Settings', perm: 'manage_settings' },
  { to: '/admin/fees/gateway',            label: 'Payment Gateway',   perm: 'manage_settings' },
];

function SettingsNavItem({ location, navigate }) {
  const { hasPermission } = usePermissions();
  const visibleItems = SETTINGS_SUB_ITEMS.filter(item => hasPermission(item.perm));
  const isSettingsActive = location.pathname.startsWith('/admin/settings') || location.pathname === '/admin/fees/gateway';
  const [open, setOpen] = React.useState(isSettingsActive);

  React.useEffect(() => {
    if (isSettingsActive) setOpen(true);
  }, [isSettingsActive]);

  // Hide the entire collapsible if no sub-items are accessible
  if (visibleItems.length === 0) return null;

  return (
    <div>
      {/* Settings header button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          ...sb.navItem,
          width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left',
          background: isSettingsActive || open ? 'rgba(255,255,255,0.18)' : 'none',
          color: isSettingsActive || open ? '#fff' : 'rgba(255,255,255,0.6)',
          fontWeight: isSettingsActive || open ? 600 : 500,
          display: 'flex', alignItems: 'center', gap: 12,
          justifyContent: 'space-between',
        }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <Settings size={18} />
          <span>Settings</span>
        </span>
        <ChevronDown size={14} style={{ transform: open ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .2s', flexShrink: 0 }} />
      </button>

      {/* Sub-items */}
      {open && (
        <div style={{ paddingLeft: 14, marginTop: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {visibleItems.map(({ to, label }) => {
            const isActive = location.pathname === to;
            return (
              <button
                key={to}
                onClick={() => navigate(to)}
                style={{
                  width: '100%', background: isActive ? 'rgba(255,255,255,0.15)' : 'none',
                  border: 'none', cursor: 'pointer', textAlign: 'left',
                  padding: '8px 12px', borderRadius: 8,
                  color: isActive ? '#fff' : 'rgba(255,255,255,0.55)',
                  fontSize: 13, fontWeight: isActive ? 600 : 400,
                  display: 'flex', alignItems: 'center', gap: 8,
                  transition: 'all .12s',
                }}
                onMouseEnter={e => { if (!isActive) e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = isActive ? 'rgba(255,255,255,0.15)' : 'none'; e.currentTarget.style.color = isActive ? '#fff' : 'rgba(255,255,255,0.55)'; }}
              >
                <span style={{ width: 4, height: 4, borderRadius: 2, background: isActive ? '#fff' : 'rgba(255,255,255,0.3)', flexShrink: 0 }} />
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* ─── Admin nav config ───────────────── */
const ADMIN_COLOR = '#1a3a5c'; // fallback only — sidebar uses school.primary_color when available

/**
 * perm: null        → always visible (no permission required)
 * perm: 'key'       → visible only if user has that permission
 * perm: ['k1','k2'] → visible if user has ANY of those permissions
 */
const ADMIN_NAV = [
  { to: '/admin',               Icon: Home,          labelKey: 'nav.dashboard',    end: true, perm: null },
  { to: '/admin/classes',       Icon: School,        labelKey: 'nav.classes',                 perm: 'manage_classes' },
  { to: '/admin/students',      Icon: GraduationCap, labelKey: 'nav.students',                perm: ['manage_students', 'view_academic_records'] },
  { to: '/admin/teachers',      Icon: ClipboardCheck,labelKey: 'nav.teachers',                perm: 'manage_teachers' },
  { to: '/admin/subjects',      Icon: BookOpen,      labelKey: 'nav.subjects',                perm: 'manage_classes' },
  { to: '/admin/parents',       Icon: Users,         labelKey: 'nav.parents',                 perm: ['manage_parent_links', 'view_academic_records'] },
  { to: '/admin/fees',          Icon: Wallet,        labelKey: 'nav.fees',                    perm: 'view_fees' },
  { to: '/admin/payroll',       Icon: Wallet,        labelKey: 'nav.payroll',                 perm: 'full_access' },
  { to: '/admin/reports',       Icon: BarChart2,     labelKey: 'nav.reports',                 perm: 'view_all_reports' },
  { to: '/admin/announcements', Icon: Megaphone,     labelKey: 'nav.announcements',           perm: 'send_announcements' },
  { to: '/admin/messages',      Icon: MessageSquare, labelKey: 'nav.messages',                perm: null },
  { to: '/admin/guide-center',  Icon: Compass,       labelKey: 'nav.guideCenter',             perm: null },
];

/* ─── NotAuthorized — shown inside AdminLayout when a permission check fails ── */
function NotAuthorized() {
  const navigate = useNavigate();
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      minHeight: '60vh', padding: '40px 20px', textAlign: 'center',
    }}>
      <div style={{ fontSize: 44, marginBottom: 16 }}>🔒</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: '#111827', marginBottom: 8 }}>
        Access Restricted
      </div>
      <div style={{ fontSize: 14, color: '#6B7280', maxWidth: 360, lineHeight: 1.6, marginBottom: 28 }}>
        You don't have permission to view this page.
        Contact your school administrator to request access.
      </div>
      <button
        onClick={() => navigate('/admin')}
        style={{
          padding: '10px 24px', background: '#1a3a5c', color: '#fff',
          border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700,
          cursor: 'pointer',
        }}
      >
        ← Go to Dashboard
      </button>
    </div>
  );
}

/**
 * PermGate — renders children if the current user has the required permission,
 * otherwise renders NotAuthorized.  Place INSIDE the layout so the sidebar
 * remains visible even on restricted pages (better UX than a full redirect).
 *
 * Usage:
 *   <PermGate perm="manage_teachers"><AdminTeachers /></PermGate>
 *   <PermGate anyPerm={['manage_students','view_academic_records']}><AdminStudents /></PermGate>
 */
function PermGate({ perm, anyPerm, children }) {
  const { hasPermission, hasAnyPermission, loading } = usePermissions();
  // While permissions are being fetched, show a neutral loader
  if (loading) return <PageLoader />;
  const allowed = anyPerm ? hasAnyPermission(...anyPerm) : hasPermission(perm);
  if (!allowed) return <NotAuthorized />;
  return children;
}

/* ─── Language Toggle ── */
function LanguageToggle({ light = false }) {
  const { i18n } = useTranslation();
  const isAm = i18n.language === 'am';
  const activeBg    = light ? 'rgba(26,58,92,0.10)' : 'rgba(255,255,255,0.25)';
  const activeColor = light ? '#1a3a5c'               : '#fff';
  const inactiveColor = light ? '#9CA3AF'             : 'rgba(255,255,255,0.45)';
  return (
    <div style={light ? {} : { padding: '4px 10px 8px', display: 'flex', justifyContent: 'center' }}>
      <button
        onClick={() => i18n.changeLanguage(isAm ? 'en' : 'am')}
        title={isAm ? 'Switch to English' : 'ወደ አማርኛ ቀይር'}
        style={{
          display: 'flex', alignItems: 'center', gap: 0,
          background: light ? 'rgba(0,0,0,0.05)' : 'rgba(255,255,255,0.10)',
          border: light ? '1px solid rgba(0,0,0,0.12)' : '1px solid rgba(255,255,255,0.18)',
          borderRadius: 20, overflow: 'hidden', cursor: 'pointer', padding: 0,
        }}
      >
        <span style={{ padding: '5px 13px', fontSize: 11, fontWeight: 700, background: !isAm ? activeBg : 'transparent', color: !isAm ? activeColor : inactiveColor, transition: 'all .15s' }}>EN</span>
        <span style={{ padding: '5px 13px', fontSize: 11, fontWeight: 700, background:  isAm ? activeBg : 'transparent', color:  isAm ? activeColor : inactiveColor, transition: 'all .15s' }}>አማ</span>
      </button>
    </div>
  );
}

/* ─── Admin Sidebar with Timetable dropdown ── */
function AdminSidebar() {
  const { user } = useAuth();
  const { profile } = useSchoolProfile();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, hasAnyPermission } = usePermissions();
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [timetableOpen, setTimetableOpen] = React.useState(false);
  const [classes, setClasses] = React.useState([]);
  const [loadingClasses, setLoadingClasses] = React.useState(false);
  const [overdueCount,    setOverdueCount]    = React.useState(0);
  const [annDraftCount,   setAnnDraftCount]   = React.useState(0);
  const [msgUnreadCount,  setMsgUnreadCount]  = React.useState(0);
  const [gradesFlagCount, setGradesFlagCount] = React.useState(0);

  // Derive display name from school profile.
  // Empty string while loading — never show a hardcoded fallback name.
  const schoolName = profile?.name || '';
  const words = schoolName.trim().split(/\s+/);
  const brandTitle = words[0] || '';
  const brandSub = words.slice(1).join(' ') || (schoolName ? 'School' : '');

  // Fetch overdue count for Fees badge
  React.useEffect(() => {
    client.get('/fees/overview').then(({ data }) => {
      setOverdueCount(data?.data?.countOverdue || 0);
    }).catch(() => {});
  }, []);

  // Fetch draft announcements count for Announcements badge
  React.useEffect(() => {
    client.get('/admin/announcements', { params: { status: 'draft' } })
      .then(({ data }) => setAnnDraftCount(Array.isArray(data) ? data.length : 0))
      .catch(() => {});
  }, []);

  // Fetch unread message count for Messages badge (poll every 30s)
  React.useEffect(() => {
    const fetchMsgCount = () => {
      client.get('/admin/messages').then(({ data }) => {
        setMsgUnreadCount(data?.unread_count || 0);
      }).catch(() => {});
    };
    fetchMsgCount();
    const interval = setInterval(fetchMsgCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Decrement badge immediately when admin reads a thread (fired by Messages page)
  React.useEffect(() => {
    const handler = () => setMsgUnreadCount(prev => Math.max(0, prev - 1));
    window.addEventListener('admin-msg-read', handler);
    return () => window.removeEventListener('admin-msg-read', handler);
  }, []);

  // Fetch pending grade change request count for Academics badge (poll every 60s)
  React.useEffect(() => {
    const fetch = () => {
      client.get('/admin/grade-flags')
        .then(({ data }) => setGradesFlagCount(data?.statuses?.pending || 0))
        .catch(() => {});
    };
    fetch();
    const iv = setInterval(fetch, 60000);
    return () => clearInterval(iv);
  }, []);

  // Clear grade flag badge when admin visits the grade-flags page
  React.useEffect(() => {
    if (location.pathname === '/admin/academics/grade-flags') setGradesFlagCount(0);
  }, [location.pathname]);

  // Clear draft badge when admin opens the Announcements page
  React.useEffect(() => {
    if (location.pathname === '/admin/announcements') setAnnDraftCount(0);
  }, [location.pathname]);


  // Fetch classes when dropdown opens
  React.useEffect(() => {
    if (!timetableOpen) return;
    setLoadingClasses(true);
    client.get('/admin/classes').then(({ data }) => {
      setClasses(Array.isArray(data) ? data : data.classes || []);
    }).catch(() => setClasses([])).finally(() => setLoadingClasses(false));
  }, [timetableOpen]);

  const isTimetableActive = location.pathname.includes('/timetable');
  const sidebarColor = profile?.primary_color || ADMIN_COLOR;

  return (
    <>
      <div style={{ ...sb.sidebar, background: sidebarColor }}>
        {/* Brand */}
        <div style={sb.brand}>
          {/* Show school logo if available, otherwise fallback to School icon */}
          {(profile?.logo_url || profile?.logo) ? (
            <img
              src={`${resolveUrl(profile.logo_url || profile.logo)}${(profile.logo_url || profile.logo).includes('?') ? '&' : '?'}cb=${logoCacheBust}`}
              alt="School logo"
              style={{
                width: 44, height: 44, borderRadius: 12, objectFit: 'cover',
                border: '2px solid rgba(255,255,255,0.3)', flexShrink: 0,
              }}
            />
          ) : (
            <div style={sb.brandIcon}><School size={20} color={sidebarColor} /></div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ ...sb.brandTitle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{brandTitle}</div>
            <div style={sb.brandSub}>{brandSub}</div>
          </div>
        </div>

        {/* Nav */}
        <nav style={sb.nav}>
          {ADMIN_NAV.map(({ to, Icon, labelKey, end, perm }) => {
            // Permission filter — hide items the user isn't allowed to access
            const allowed = Array.isArray(perm)
              ? hasAnyPermission(...perm)
              : hasPermission(perm);
            if (!allowed) return null;

            const label = t(labelKey);
            const badge =
              labelKey === 'nav.fees'          ? overdueCount    :
              labelKey === 'nav.announcements' ? annDraftCount   :
              labelKey === 'nav.messages'      ? msgUnreadCount  :
              0;
            return (
              <NavLink key={to} to={to} end={end}
                style={({ isActive }) => ({ ...sb.navItem, ...(isActive ? sb.navActive : {}) })}>
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <Icon size={18} />
                  {badge > 0 && (
                    <div style={{
                      position: 'absolute', top: -5, right: -6,
                      minWidth: 16, height: 16, borderRadius: 8,
                      background: '#EF4444', color: '#fff',
                      fontSize: 9, fontWeight: 800,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      padding: '0 3px', border: `2px solid ${sidebarColor}`,
                      lineHeight: 1,
                    }}>
                      {badge > 99 ? '99+' : badge}
                    </div>
                  )}
                </div>
                <span style={{ flex: 1 }}>{label}</span>
                {badge > 0 && (
                  <span style={{
                    background: '#EF4444', color: '#fff', fontSize: 10, fontWeight: 800,
                    borderRadius: 10, padding: '1px 6px', minWidth: 18, textAlign: 'center',
                    lineHeight: '16px', flexShrink: 0,
                  }}>
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </NavLink>
            );
          })}

          {/* ── Academic Years collapsible nav ── */}
          <AcademicNavItem location={location} navigate={navigate} gradesFlagCount={gradesFlagCount} />

          {/* ── Settings collapsible nav ── */}
          <SettingsNavItem location={location} navigate={navigate} />

          {/* ── Timetable with class list — requires manage_classes ── */}
          {/* Inline expand (no absolute positioning) so it works on small screens too */}
          {hasPermission('manage_classes') && <div>
            <button
              onClick={() => setTimetableOpen(p => !p)}
              style={{
                ...sb.navItem,
                width: '100%', border: 'none', cursor: 'pointer', textAlign: 'left',
                background: isTimetableActive || timetableOpen ? 'rgba(255,255,255,0.18)' : 'none',
                color: isTimetableActive || timetableOpen ? '#fff' : 'rgba(255,255,255,0.6)',
                fontWeight: isTimetableActive || timetableOpen ? 600 : 500,
                display: 'flex', alignItems: 'center', gap: 12,
                justifyContent: 'space-between',
              }}>
              <span style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <Calendar size={18} />
                <span>{t('nav.timetable')}</span>
              </span>
              <ChevronDown size={14} style={{ transform: timetableOpen ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform .2s', flexShrink: 0 }}/>
            </button>

            {/* Inline class list — expands in flow so overflow:auto nav can scroll to it */}
            {timetableOpen && (
              <div style={{
                background: 'rgba(0,0,0,0.25)', borderRadius: 10, margin: '2px 0 4px',
                border: '1px solid rgba(255,255,255,0.08)',
              }}>
                {loadingClasses ? (
                  <div style={{ padding: '12px 14px', fontSize: 12, color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>Loading classes…</div>
                ) : classes.length === 0 ? (
                  <div style={{ padding: '12px 14px', fontSize: 12, color: 'rgba(255,255,255,0.5)', textAlign: 'center' }}>No classes found</div>
                ) : (
                  classes.map(cls => (
                    <button
                      key={cls._id}
                      onClick={() => { setTimetableOpen(false); navigate(`/admin/classes/${cls._id}/timetable`); }}
                      style={{
                        width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                        padding: '9px 14px', textAlign: 'left', display: 'flex', alignItems: 'center', gap: 10,
                        color: 'rgba(255,255,255,0.85)', fontSize: 13, fontWeight: 500,
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                        transition: 'background .1s',
                      }}
                      onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.12)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'none'}
                    >
                      <div style={{
                        width: 28, height: 28, borderRadius: 8, flexShrink: 0,
                        background: 'rgba(255,255,255,0.15)',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 10, fontWeight: 800, color: '#fff',
                      }}>
                        {(cls.name || cls.grade || '?').toString().slice(0, 3).toUpperCase()}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cls.name || `Class ${cls.grade}`}</div>
                        {cls.academic_year && <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.45)', marginTop: 1 }}>{cls.academic_year}</div>}
                      </div>
                      <ChevronRight size={12} color="rgba(255,255,255,0.4)"/>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>}
        </nav>

        {/* User row */}
        <div style={sb.userRow} onClick={() => setProfileOpen(true)}>
          <div style={{ ...sb.userAvatar, background: getAvatarColor(user?.full_name || '') }}>{getInitials(user?.full_name || '')}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={sb.userName}>{user?.full_name || '–'}</div>
            <div style={sb.userRole}>{user?.role || '–'}</div>
          </div>
        </div>
      </div>
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}

/* ─── Parent nav config ──────────────── */
const PARENT_NAV = [
  { to: '/parent',             Icon: Home,          label: 'Home',       end: true },
  { to: '/parent/attendance',  Icon: Calendar,      label: 'Attendance'            },
  { to: '/parent/grades',      Icon: Ribbon,        label: 'Grades'                },
  { to: '/parent/homework',    Icon: BookOpen,      label: 'Homework/Assignment'   },
  { to: '/parent/timetable',   Icon: Clock,         label: 'Timetable'             },
  { to: '/parent/messages',    Icon: MessageSquare, label: 'Messages'              },
  { to: '/parent/fees',        Icon: Wallet,        label: 'Fees'                  },
  { to: '/parent/profile',     Icon: User,          label: 'Profile'               },
];

const PARENT_BOTTOM_NAV = [
  { to: '/parent',            Icon: Home,          label: 'Home',    end: true },
  { to: '/parent/homework',   Icon: BookOpen,      label: 'Homework/Assignment' },
  { to: '/parent/messages',   Icon: MessageSquare, label: 'Messages'           },
  { to: '/parent/fees',       Icon: Wallet,        label: 'Fees'               },
  { to: '/parent/profile',    Icon: User,          label: 'Profile'            },
];


/* ─── Student nav config ─────────────── */
const STUDENT_NAV = [
  { to: '/student', Icon: Home, label: 'Dashboard', end: true },
  { to: '/student/homework', Icon: BookOpen, label: 'Homework/Assignment' },
  { to: '/student/grades', Icon: GraduationCap, label: 'Grades' },
  { to: '/student/attendance', Icon: Calendar, label: 'Attendance' },
  { to: '/student/timetable', Icon: Clock, label: 'Timetable' },
  { to: '/student/announcements', Icon: Bell, label: 'Announcements' },
  { to: '/student/messages', Icon: MessageSquare, label: 'Messages' },
  { to: '/student/profile', Icon: User, label: 'Profile' },
];

const STUDENT_COLOR = '#4F46E5';

/* ─── Profile Modal ──────────────────────── */
const ROLE_META = {
  teacher:       { bg: '#D1FAE5', text: '#059669', label: 'Teacher'       },
  parent:        { bg: '#DBEAFE', text: '#1D4ED8', label: 'Parent'        },
  student:       { bg: '#EEF2FF', text: '#4F46E5', label: 'Student'       },
  admin:     { bg: '#F3F4F6', text: '#374151', label: 'Admin'     },
  registrar: { bg: '#FCE7F3', text: '#9D174D', label: 'Registrar' },
  cashier:   { bg: '#FEF3C7', text: '#B45309', label: 'Cashier'   },
};

function ProfileModal({ open, onClose }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!open) return null;
  const meta = ROLE_META[user?.role] || ROLE_META.teacher;

  return (
    <div style={pm.overlay} onClick={onClose}>
      <div style={pm.sheet} onClick={e => e.stopPropagation()}>
        <div style={pm.userRow}>
          <div style={{ ...pm.avatar, background: getAvatarColor(user?.full_name || '') }}>{getInitials(user?.full_name || '')}</div>
          <div>
            <div style={pm.userName}>{user?.full_name}</div>
            {user?.phone && <div style={pm.userPhone}>{user.phone}</div>}
            <span style={{ ...pm.roleBadge, background: meta.bg, color: meta.text }}>{meta.label}</span>
          </div>
        </div>
        <div style={pm.divider} />
        <button style={pm.menuBtn} onClick={() => { onClose(); navigate('/change-password'); }}>🔒 Change Password</button>
        <button style={{ ...pm.menuBtn, color: '#DC2626' }} onClick={() => { onClose(); logout(); navigate('/login'); }}>
          ↪ Log Out / ወጣ
        </button>
      </div>
    </div>
  );
}

const pm = {
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  sheet: { background: '#fff', borderRadius: 20, padding: 24, width: 400, maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto' },
  userRow: { display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 },
  avatar: { width: 56, height: 56, borderRadius: 28, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 20, flexShrink: 0 },
  userName: { fontSize: 17, fontWeight: 700 },
  userPhone: { fontSize: 13, color: '#6B7280' },
  roleBadge: { display: 'inline-block', fontSize: 12, fontWeight: 600, borderRadius: 10, padding: '3px 10px', marginTop: 4 },
  divider: { height: 1, background: '#F3F4F6', margin: '16px 0' },
  menuBtn: { display: 'block', width: '100%', background: 'none', border: 'none', padding: '12px 0', fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'left' },
};

/* ─── Parent / Student Sidebar ──────────── */
function Sidebar({ items, themeColor, badges = {} }) {
  const { user } = useAuth();
  const { profile } = useSchoolProfile();
  const [profileOpen, setProfileOpen] = React.useState(false);
  const [logoErr, setLogoErr] = React.useState(false);

  const schoolName = profile?.name || '';
  const words = schoolName.trim().split(/\s+/);
  const brandTitle = words[0] || '';
  const brandSub = words.slice(1).join(' ') || (schoolName ? 'School' : '');
  const logoUrl = profile?.logo_url || profile?.logo || '';

  return (
    <>
      <div style={{ ...sb.sidebar, background: themeColor }}>
        <div style={sb.brand}>
          {logoUrl && !logoErr ? (
            <img
              src={`${resolveUrl(logoUrl)}${logoUrl.includes('?') ? '&' : '?'}cb=${logoCacheBust}`}
              alt="School logo"
              onError={() => setLogoErr(true)}
              style={{ width: 38, height: 38, borderRadius: 10, objectFit: 'cover', border: '2px solid rgba(255,255,255,0.3)', flexShrink: 0 }}
            />
          ) : (
            <div style={sb.brandIcon}><School size={20} color={themeColor} /></div>
          )}
          <div style={{ minWidth: 0 }}>
            <div style={{ ...sb.brandTitle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 140 }}>{brandTitle}</div>
            <div style={sb.brandSub}>{brandSub}</div>
          </div>
        </div>
        <nav style={sb.nav}>
          {items.map(({ to, Icon, label, end }) => {
            const count = badges[to] || 0;
            return (
              <NavLink key={to} to={to} end={end} style={({ isActive }) => ({ ...sb.navItem, ...(isActive ? sb.navActive : {}) })}>
                {({ isActive }) => (
                  <>
                    <div style={{ position: 'relative', flexShrink: 0 }}>
                      <Icon size={18} />
                      {count > 0 && (
                        <div style={{
                          position: 'absolute', top: -5, right: -6,
                          minWidth: 16, height: 16, borderRadius: 8,
                          background: '#EF4444', color: '#fff',
                          fontSize: 9, fontWeight: 800,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          padding: '0 3px', border: `2px solid ${themeColor}`,
                          lineHeight: 1,
                        }}>
                          {count > 99 ? '99+' : count}
                        </div>
                      )}
                    </div>
                    <span style={{ flex: 1 }}>{label}</span>
                    {count > 0 && !isActive && (
                      <span style={{
                        fontSize: 10, fontWeight: 800,
                        background: 'rgba(239,68,68,0.2)',
                        color: '#FCA5A5',
                        borderRadius: 10, padding: '1px 6px',
                        flexShrink: 0,
                      }}>
                        {count}
                      </span>
                    )}
                  </>
                )}
              </NavLink>
            );
          })}
        </nav>
        <div style={sb.userRow} onClick={() => setProfileOpen(true)}>
          <div style={{ ...sb.userAvatar, background: getAvatarColor(user?.full_name || '') }}>{getInitials(user?.full_name || '')}</div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={sb.userName}>{user?.full_name || '–'}</div>
            <div style={sb.userRole}>{user?.role || '–'}</div>
          </div>
        </div>
      </div>
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}

const sb = {
  sidebar: { width: 220, display: 'flex', flexDirection: 'column', flexShrink: 0, height: '100vh' },
  brand: { display: 'flex', alignItems: 'center', gap: 10, padding: '20px 16px', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: 8 },
  brandIcon: { width: 38, height: 38, borderRadius: 10, background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  brandTitle: { fontSize: 17, fontWeight: 800, color: '#fff', letterSpacing: 0.5 },
  brandSub: { fontSize: 10, color: 'rgba(255,255,255,0.65)', letterSpacing: 1.5, textTransform: 'uppercase' },
  nav: { flex: 1, minHeight: 0, padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 2, overflowY: 'auto' },
  navItem: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', borderRadius: 10, color: 'rgba(255,255,255,0.6)', textDecoration: 'none', fontSize: 14, fontWeight: 500, cursor: 'pointer', transition: 'all .12s' },
  navActive: { background: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 600 },
  userRow: { display: 'flex', alignItems: 'center', gap: 10, padding: '14px 14px 18px', margin: '0 10px', borderRadius: 12, background: 'rgba(0,0,0,0.15)', cursor: 'pointer' },
  userAvatar: { width: 34, height: 34, borderRadius: 17, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontWeight: 700, fontSize: 13, flexShrink: 0 },
  userName: { fontSize: 13, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  userRole: { fontSize: 10, color: 'rgba(255,255,255,0.65)', textTransform: 'capitalize', marginTop: 1 },
};

/* ─── Mobile detection ───────────────────── */
function useIsMobile(bp = 768) {
  const [mobile, setMobile] = React.useState(() => window.matchMedia(`(max-width: ${bp}px)`).matches);
  React.useEffect(() => {
    const mql = window.matchMedia(`(max-width: ${bp}px)`);
    const h = (e) => setMobile(e.matches);
    mql.addEventListener('change', h);
    setMobile(mql.matches);
    return () => mql.removeEventListener('change', h);
  }, [bp]);
  return mobile;
}

/* ─── Student bottom nav (mobile only) ───── */
const STUDENT_PRIMARY_NAV = [
  { to: '/student',            Icon: Home,          label: 'Home',      end: true },
  { to: '/student/homework',   Icon: BookOpen,      label: 'Homework/Assignment'  },
  { to: '/student/grades',     Icon: GraduationCap, label: 'Grades'               },
  { to: '/student/attendance', Icon: Calendar,      label: 'Attendance'           },
];

const STUDENT_MORE_NAV = [
  { to: '/student/timetable',     Icon: Clock,          label: 'Timetable'     },
  { to: '/student/announcements', Icon: Bell,           label: 'Announcements' },
  { to: '/student/messages',      Icon: MessageSquare,  label: 'Messages'      },
  { to: '/student/profile',       Icon: User,           label: 'Profile'       },
];

function StudentBottomNav({ annCount = 0, msgCount = 0 }) {
  const { color } = useSchoolBrand();
  const [moreOpen, setMoreOpen] = React.useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  React.useEffect(() => { setMoreOpen(false); }, [location.pathname]);

  const moreActive = STUDENT_MORE_NAV.some(({ to }) => location.pathname.startsWith(to));

  return (
    <>
      {/* Backdrop */}
      {moreOpen && (
        <div
          onClick={() => setMoreOpen(false)}
          style={{ position: 'fixed', inset: 0, zIndex: 198, background: 'rgba(0,0,0,0.35)' }}
        />
      )}

      {/* More sheet — slides up just above the nav bar (vertical list) */}
      <div style={{
        position: 'fixed', left: 0, right: 0, zIndex: 199,
        bottom: moreOpen ? 64 : -(STUDENT_MORE_NAV.length * 60 + 48),
        background: '#fff',
        borderRadius: '16px 16px 0 0',
        boxShadow: '0 -4px 24px rgba(0,0,0,0.12)',
        padding: '12px 0 8px',
        transition: 'bottom 0.25s cubic-bezier(0.4,0,0.2,1)',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 1, padding: '0 20px 10px' }}>More</div>
        {STUDENT_MORE_NAV.map(({ to, Icon, label }) => {
          const isActive = location.pathname.startsWith(to);
          const badgeCount = label === 'Announcements' ? annCount : label === 'Messages' ? msgCount : 0;
          return (
            <button key={to}
              onClick={() => { navigate(to); setMoreOpen(false); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 16,
                padding: '14px 20px',
                background: isActive ? `${color}10` : 'none',
                border: 'none', borderBottom: '1px solid #F3F4F6',
                cursor: 'pointer', textAlign: 'left', width: '100%',
                color: isActive ? color : '#374151',
              }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12, flexShrink: 0,
                background: isActive ? `${color}20` : '#F3F4F6',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                position: 'relative',
              }}>
                <Icon size={20} strokeWidth={isActive ? 2.5 : 1.8} />
                {badgeCount > 0 && (
                  <div style={{
                    position: 'absolute', top: -4, right: -4,
                    minWidth: 16, height: 16, borderRadius: 8,
                    background: '#EF4444', color: '#fff',
                    fontSize: 9, fontWeight: 800,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    padding: '0 3px', border: '2px solid #fff',
                  }}>
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </div>
                )}
              </div>
              <span style={{ fontSize: 15, fontWeight: isActive ? 700 : 500 }}>{label}</span>
              {isActive && <div style={{ marginLeft: 'auto', width: 6, height: 6, borderRadius: '50%', background: color }} />}
            </button>
          );
        })}
      </div>

      {/* Bottom nav bar */}
      <nav style={{
        position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 200,
        background: '#fff', borderTop: '1px solid #E5E7EB',
        boxShadow: '0 -2px 12px rgba(0,0,0,0.08)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        display: 'flex',
      }}>
        {STUDENT_PRIMARY_NAV.map(({ to, Icon, label, end }) => (
          <NavLink key={to} to={to} end={end}
            style={({ isActive }) => ({
              flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
              justifyContent: 'center', gap: 3, padding: '8px 4px',
              textDecoration: 'none',
              color: isActive ? color : '#9CA3AF',
              borderTop: isActive ? `2px solid ${color}` : '2px solid transparent',
            })}>
            {({ isActive }) => (
              <>
                <Icon size={20} strokeWidth={isActive ? 2.5 : 1.8} />
                <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500, letterSpacing: 0.2 }}>{label}</span>
              </>
            )}
          </NavLink>
        ))}

        {/* More button */}
        <button
          onClick={() => setMoreOpen(o => !o)}
          style={{
            flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center',
            justifyContent: 'center', gap: 3, padding: '8px 4px',
            background: 'none', border: 'none', cursor: 'pointer',
            borderTop: (moreActive || moreOpen) ? `2px solid ${color}` : '2px solid transparent',
            color: (moreActive || moreOpen) ? color : '#9CA3AF',
          }}>
          <div style={{ position: 'relative' }}>
            <MoreHorizontal size={20} strokeWidth={(moreActive || moreOpen) ? 2.5 : 1.8} />
            {(annCount + msgCount) > 0 && !moreOpen && (
              <div style={{
                position: 'absolute', top: -4, right: -6,
                minWidth: 15, height: 15, borderRadius: 8,
                background: '#EF4444', color: '#fff',
                fontSize: 8, fontWeight: 800,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '0 3px', border: '1.5px solid #fff',
              }}>
                {(annCount + msgCount) > 99 ? '99+' : (annCount + msgCount)}
              </div>
            )}
          </div>
          <span style={{ fontSize: 10, fontWeight: (moreActive || moreOpen) ? 700 : 500, letterSpacing: 0.2 }}>More</span>
        </button>
      </nav>
    </>
  );
}

/* ─── Mobile redirect for admin/registrar roles ──────────────────────────────
   Admin portal is desktop-only (fixed-width tables, hover interactions).
   When opened on a phone or tablet in the native app, show a friendly message
   instead of a broken layout. Teachers/students/parents are unaffected.
─────────────────────────────────────────────────────────────────────────────── */
function AdminMobileWarning({ onContinue }) {
  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 9999,
      background: 'linear-gradient(135deg, #1a3a5c 0%, #0d1b2a 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: 24,
    }}>
      <div style={{
        background: '#fff', borderRadius: 24, padding: '36px 28px',
        maxWidth: 360, width: '100%', textAlign: 'center',
        boxShadow: '0 24px 64px rgba(0,0,0,0.4)',
      }}>
        <div style={{ fontSize: 52, marginBottom: 16 }}>🖥️</div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1a3a5c', margin: '0 0 10px' }}>
          Desktop Recommended
        </h2>
        <p style={{ fontSize: 14, color: '#6B7280', lineHeight: 1.7, margin: '0 0 24px' }}>
          The admin portal is designed for desktop browsers. On a small screen, some features may be difficult to use or may not display correctly.
        </p>
        <div style={{ background: '#FEF3C7', border: '1px solid #FCD34D', borderRadius: 12, padding: '12px 16px', marginBottom: 24, textAlign: 'left' }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#92400E', marginBottom: 4 }}>For the best experience:</div>
          <div style={{ fontSize: 12, color: '#78350F', lineHeight: 1.6 }}>
            Open <strong>addiscoreet.com</strong> on a laptop or desktop computer.
          </div>
        </div>
        <button
          onClick={onContinue}
          style={{
            width: '100%', padding: '13px', borderRadius: 12,
            background: '#1a3a5c', border: 'none', color: '#fff',
            fontSize: 14, fontWeight: 700, cursor: 'pointer', marginBottom: 10,
          }}
        >
          I understand, continue anyway
        </button>
      </div>
    </div>
  );
}

/* ─── Layouts ────────────────────────────── */
function AdminLayout({ children }) {
  const { user } = useAuth();
  const isMobile = window.innerWidth <= 900;
  const isImpersonating = !!(() => { try { return JSON.parse(localStorage.getItem('impersonating') || 'null'); } catch { return null; } })();
  const [warningAcknowledged, setWarningAcknowledged] = React.useState(
    () => sessionStorage.getItem('admin_mobile_warned') === 'yes'
  );

  const handleContinue = () => {
    sessionStorage.setItem('admin_mobile_warned', 'yes');
    setWarningAcknowledged(true);
  };

  const { logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100vh', background: '#f5f5f0' }}>
      {isMobile && !warningAcknowledged && <AdminMobileWarning onContinue={handleContinue} />}
      {isMobile && warningAcknowledged && (
        <div style={{
          background: '#1a3a5c', paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 16, paddingRight: 16, paddingBottom: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Admin Portal</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{user?.full_name}</div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <LanguageToggle />
            <button
              onClick={handleLogout}
              style={{
                background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)',
                borderRadius: 8, padding: '6px 14px', color: '#fff',
                fontSize: 12, fontWeight: 700, cursor: 'pointer',
              }}
            >
              Logout
            </button>
          </div>
        </div>
      )}
      <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
        <ImpersonationBanner />
        {!isMobile && <AdminSidebar />}
        {isMobile
          ? <main style={{ flex: 1, overflow: 'auto' }}>{children}</main>
          : (
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', marginTop: isImpersonating ? 44 : 0 }}>
              {/* Desktop top header */}
              <div style={{
                height: 44, flexShrink: 0,
                background: '#fff',
                borderBottom: '1px solid rgba(0,0,0,0.07)',
                display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
                paddingRight: 20, gap: 12,
              }}>
                <LanguageToggle light />
              </div>
              <main style={{ flex: 1, overflow: 'auto' }}>{children}</main>
            </div>
          )
        }
      </div>
    </div>
  );
}

/* ─── Registrar sidebar ──────────────────────────────────────────────────────── */
const REGISTRAR_COLOR = '#0F766E'; // teal-700 fallback; overridden by school primary_color

const REGISTRAR_STUDENTS_SUB = [
  { to: '/registrar/students',   label: 'Student Records' },
  { to: '/registrar/enrollment', label: 'Enrollment'      },
  { to: '/registrar/transfers',  label: 'Transfers'       },
];

function RegistrarStudentsNav({ location, navigate }) {
  const isActive = ['/registrar/students', '/registrar/enrollment', '/registrar/transfers']
    .some(p => location.pathname.startsWith(p));
  const [open, setOpen] = React.useState(isActive);
  React.useEffect(() => { if (isActive) setOpen(true); }, [isActive]);

  return (
    <div>
      <button
        onClick={() => setOpen(o => !o)}
        style={{ width:'100%', background: isActive ? 'rgba(255,255,255,0.18)' : 'none', border:'none', cursor:'pointer', textAlign:'left', padding:'10px 12px', borderRadius:10, color: isActive ? '#fff' : 'rgba(255,255,255,0.6)', fontSize:14, fontWeight: isActive ? 600 : 500, display:'flex', alignItems:'center', gap:12, transition:'all .12s' }}
        onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}}
        onMouseLeave={e => { e.currentTarget.style.background = isActive ? 'rgba(255,255,255,0.18)' : 'none'; e.currentTarget.style.color = isActive ? '#fff' : 'rgba(255,255,255,0.6)'; }}
      >
        <GraduationCap size={18} />
        <span style={{ flex:1 }}>Students</span>
        {open ? <ChevronDown size={14}/> : <ChevronRight size={14}/>}
      </button>
      {open && (
        <div style={{ paddingLeft:16, marginTop:2, display:'flex', flexDirection:'column', gap:1 }}>
          {REGISTRAR_STUDENTS_SUB.map(({ to, label }) => {
            const sub = location.pathname === to;
            return (
              <button key={to} onClick={() => navigate(to)}
                style={{ width:'100%', background: sub ? 'rgba(255,255,255,0.15)' : 'none', border:'none', cursor:'pointer', textAlign:'left', padding:'8px 12px', borderRadius:8, color: sub ? '#fff' : 'rgba(255,255,255,0.55)', fontSize:13, fontWeight: sub ? 600 : 400, display:'flex', alignItems:'center', gap:8, transition:'all .12s' }}
                onMouseEnter={e => { if (!sub) { e.currentTarget.style.background = 'rgba(255,255,255,0.08)'; e.currentTarget.style.color = 'rgba(255,255,255,0.85)'; }}}
                onMouseLeave={e => { e.currentTarget.style.background = sub ? 'rgba(255,255,255,0.15)' : 'none'; e.currentTarget.style.color = sub ? '#fff' : 'rgba(255,255,255,0.55)'; }}
              >
                <span style={{ width:4, height:4, borderRadius:2, background: sub ? '#fff' : 'rgba(255,255,255,0.3)', flexShrink:0 }} />
                {label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function RegistrarSidebar() {
  const { user } = useAuth();
  const { profile } = useSchoolProfile();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [profileOpen,    setProfileOpen]    = React.useState(false);
  const [msgUnreadCount, setMsgUnreadCount] = React.useState(0);

  const schoolName  = profile?.name || '';
  const words       = schoolName.trim().split(/\s+/);
  const brandTitle  = words[0] || '';
  const brandSub    = words.slice(1).join(' ') || (schoolName ? 'School' : '');
  const sidebarColor = profile?.primary_color || REGISTRAR_COLOR;

  // Fetch unread message count (poll every 30s)
  React.useEffect(() => {
    const fetch = () => client.get('/admin/messages').then(({ data }) => setMsgUnreadCount(data?.unread_count || 0)).catch(() => {});
    fetch();
    const id = setInterval(fetch, 30000);
    return () => clearInterval(id);
  }, []);

  // Decrement badge when registrar reads a thread
  React.useEffect(() => {
    const h = () => setMsgUnreadCount(p => Math.max(0, p - 1));
    window.addEventListener('admin-msg-read', h);
    return () => window.removeEventListener('admin-msg-read', h);
  }, []);

  return (
    <>
      <div style={{ ...sb.sidebar, background: sidebarColor }}>
        {/* Brand */}
        <div style={sb.brand}>
          {(profile?.logo_url || profile?.logo) ? (
            <img
              src={`${resolveUrl(profile.logo_url || profile.logo)}${(profile.logo_url || profile.logo).includes('?') ? '&' : '?'}cb=${logoCacheBust}`}
              alt="School logo"
              style={{ width:44, height:44, borderRadius:12, objectFit:'cover', border:'2px solid rgba(255,255,255,0.3)', flexShrink:0 }}
            />
          ) : (
            <div style={sb.brandIcon}><School size={20} color={sidebarColor} /></div>
          )}
          <div style={{ minWidth:0 }}>
            <div style={{ ...sb.brandTitle, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:140 }}>{brandTitle}</div>
            <div style={sb.brandSub}>{brandSub}</div>
          </div>
        </div>

        {/* Nav */}
        <nav style={sb.nav}>
          <NavLink to="/registrar/dashboard" end style={({ isActive }) => ({ ...sb.navItem, ...(isActive ? sb.navActive : {}) })}>
            <Home size={18} /><span style={{ flex:1 }}>Dashboard</span>
          </NavLink>

          <RegistrarStudentsNav location={location} navigate={navigate} />

          <NavLink to="/registrar/parents" style={({ isActive }) => ({ ...sb.navItem, ...(isActive ? sb.navActive : {}) })}>
            <Users size={18} /><span style={{ flex:1 }}>Parents</span>
          </NavLink>

          <NavLink to="/registrar/academics" style={({ isActive }) => ({ ...sb.navItem, ...(isActive ? sb.navActive : {}) })}>
            <BookOpen size={18} /><span style={{ flex:1 }}>Academics</span>
          </NavLink>

          <NavLink to="/registrar/reports" style={({ isActive }) => ({ ...sb.navItem, ...(isActive ? sb.navActive : {}) })}>
            <BarChart2 size={18} /><span style={{ flex:1 }}>Reports</span>
          </NavLink>

          <NavLink to="/registrar/messages" style={({ isActive }) => ({ ...sb.navItem, ...(isActive ? sb.navActive : {}) })}>
            <div style={{ position:'relative', flexShrink:0 }}>
              <MessageSquare size={18} />
              {msgUnreadCount > 0 && (
                <div style={{ position:'absolute', top:-5, right:-6, minWidth:16, height:16, borderRadius:8, background:'#EF4444', color:'#fff', fontSize:9, fontWeight:800, display:'flex', alignItems:'center', justifyContent:'center', padding:'0 3px', border:`2px solid ${sidebarColor}`, lineHeight:1 }}>
                  {msgUnreadCount > 99 ? '99+' : msgUnreadCount}
                </div>
              )}
            </div>
            <span style={{ flex:1 }}>Messages</span>
            {msgUnreadCount > 0 && (
              <span style={{ background:'#EF4444', color:'#fff', fontSize:10, fontWeight:800, borderRadius:10, padding:'1px 6px', minWidth:18, textAlign:'center', lineHeight:'16px', flexShrink:0 }}>
                {msgUnreadCount > 99 ? '99+' : msgUnreadCount}
              </span>
            )}
          </NavLink>

          <NavLink to="/registrar/profile" style={({ isActive }) => ({ ...sb.navItem, ...(isActive ? sb.navActive : {}) })}>
            <User size={18} /><span style={{ flex:1 }}>Profile</span>
          </NavLink>
        </nav>

        {/* User row */}
        <div style={sb.userRow} onClick={() => setProfileOpen(true)}>
          <div style={{ ...sb.userAvatar, background: getAvatarColor(user?.full_name || '') }}>{getInitials(user?.full_name || '')}</div>
          <div style={{ flex:1, minWidth:0 }}>
            <div style={sb.userName}>{user?.full_name || '–'}</div>
            <div style={sb.userRole}>Registrar</div>
          </div>
        </div>
      </div>
      <ProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}

function RegistrarLayout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isMobile = window.innerWidth <= 900;
  const [warningAcknowledged, setWarningAcknowledged] = React.useState(
    () => sessionStorage.getItem('admin_mobile_warned') === 'yes'
  );

  const handleContinue = () => {
    sessionStorage.setItem('admin_mobile_warned', 'yes');
    setWarningAcknowledged(true);
  };

  const handleLogout = () => { logout(); navigate('/login'); };

  return (
    <div style={{ display:'flex', flexDirection:'column', height:'100vh', background:'#f5f5f0' }}>
      {isMobile && !warningAcknowledged && <AdminMobileWarning onContinue={handleContinue} />}
      {isMobile && warningAcknowledged && (
        <div style={{
          background: '#0F766E', paddingTop: 'env(safe-area-inset-top)',
          paddingLeft: 16, paddingRight: 16, paddingBottom: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Registrar Portal</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>{user?.full_name}</div>
          </div>
          <button onClick={handleLogout} style={{ background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.25)', borderRadius: 8, padding: '6px 14px', color: '#fff', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>
            Logout
          </button>
        </div>
      )}
      <div style={{ display:'flex', flex:1, overflow:'hidden' }}>
        {!isMobile && <RegistrarSidebar />}
        <main style={{ flex:1, overflow:'auto' }}>{children}</main>
      </div>
    </div>
  );
}

/**
 * RoleAwareAdminLayout — used for pages (parent detail, academic profile, etc.)
 * that may be navigated to from either the admin or the registrar portal.
 * Picks the correct sidebar based on the current user's role.
 */
function RoleAwareAdminLayout({ children }) {
  const { user } = useAuth();
  if (user?.role === 'registrar') return <RegistrarLayout>{children}</RegistrarLayout>;
  return <AdminLayout>{children}</AdminLayout>;
}

function TeacherLayout({ children }) {
  return <TeacherPhoneFrame>{children}</TeacherPhoneFrame>;
}

function ParentBottomNav({ annCount = 0, msgCount = 0 }) {
  const { color } = useSchoolBrand();
  return (
    <nav style={{ position: 'fixed', bottom: 0, left: 0, right: 0, background: '#fff', borderTop: '1px solid #E5E7EB', display: 'flex', zIndex: 200, boxShadow: '0 -2px 12px rgba(0,0,0,0.08)', paddingBottom: 'env(safe-area-inset-bottom)' }}>
      {PARENT_BOTTOM_NAV.map(({ to, Icon, label, end }) => {
        const badgeCount = label === 'Home' ? annCount : label === 'Messages' ? msgCount : 0;
        return (
          <NavLink key={to} to={to} end={end}
            style={({ isActive }) => ({ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 3, padding: '8px 4px', textDecoration: 'none', color: isActive ? color : '#9CA3AF', borderTop: isActive ? `2px solid ${color}` : '2px solid transparent' })}>
            {({ isActive }) => (
              <>
                <div style={{ position: 'relative' }}>
                  <Icon size={20} strokeWidth={isActive ? 2.5 : 1.8} />
                  {badgeCount > 0 && (
                    <div style={{ position: 'absolute', top: -4, right: -6, minWidth: 15, height: 15, borderRadius: 8, background: '#EF4444', color: '#fff', fontSize: 8, fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '0 3px', border: '1.5px solid #fff' }}>
                      {badgeCount > 99 ? '99+' : badgeCount}
                    </div>
                  )}
                </div>
                <span style={{ fontSize: 10, fontWeight: isActive ? 700 : 500, letterSpacing: 0.2 }}>{label}</span>
              </>
            )}
          </NavLink>
        );
      })}
    </nav>
  );
}

function ParentLayoutInner({ children }) {
  const { color } = useSchoolBrand();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const [annCount, setAnnCount] = React.useState(0);
  const [msgCount, setMsgCount] = React.useState(0);
  const schoolId = user?.school_id;

  React.useEffect(() => {
    client.get('/parent/announcements')
      .then(({ data }) => {
        const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const recent = (data || []).filter(a => new Date(a.createdAt).getTime() > cutoff);
        setAnnCount(recent.length);
      })
      .catch(() => {});
  }, []);

  // Fetch unread message count (poll every 30s)
  React.useEffect(() => {
    const fetchMsgCount = () => {
      client.get('/parent/messages')
        .then(({ data }) => {
          const unread = (data || []).filter(t =>
            t.unread_for_parent === true || (typeof t.unread_for_parent === 'number' && t.unread_for_parent > 0)
          ).length;
          setMsgCount(unread);
        })
        .catch(() => {});
    };
    fetchMsgCount();
    const interval = setInterval(fetchMsgCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Decrement badge immediately when parent reads a thread
  React.useEffect(() => {
    const handler = () => setMsgCount(prev => Math.max(0, prev - 1));
    window.addEventListener('parent-msg-read', handler);
    return () => window.removeEventListener('parent-msg-read', handler);
  }, []);

  // Clear announcement badge when parent visits Home or announcements page
  React.useEffect(() => {
    if (location.pathname === '/parent' || location.pathname === '/parent/announcements') setAnnCount(0);
  }, [location.pathname]);

  return (
    <div style={{ display: 'flex', height: '100vh', boxSizing: 'border-box', paddingTop: 'env(safe-area-inset-top)', background: COLORS.bg }}>
      {!isMobile && <Sidebar items={PARENT_NAV} themeColor={color} badges={{ '/parent': annCount, '/parent/messages': msgCount }} />}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {isMobile && (
          <div style={{ background: color || '#1A73E8', paddingLeft: 16, paddingRight: 16, paddingTop: 10, paddingBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>{user?.full_name}</div>
            <span style={{ fontSize: 11, fontWeight: 700, background: 'rgba(255,255,255,0.22)', color: '#fff', borderRadius: 20, paddingTop: 3, paddingBottom: 3, paddingLeft: 10, paddingRight: 10, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Parent</span>
          </div>
        )}
        <main style={{ flex: 1, overflow: 'auto', paddingBottom: isMobile ? 'calc(64px + env(safe-area-inset-bottom))' : 0 }}>{children}</main>
      </div>
      {isMobile && <ParentBottomNav annCount={annCount} msgCount={msgCount} />}
    </div>
  );
}

function ParentLayout({ children }) {
  return (
    <SchoolBrandProvider>
      <ParentLayoutInner>{children}</ParentLayoutInner>
    </SchoolBrandProvider>
  );
}

function StudentLayoutInner({ children }) {
  const { color } = useSchoolBrand();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const [annCount, setAnnCount] = React.useState(0);
  const [msgCount, setMsgCount] = React.useState(0);
  const schoolId = user?.school_id;

  React.useEffect(() => {
    client.get('/student/announcements')
      .then(({ data }) => {
        const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
        const recent = (data || []).filter(a => a.source === 'announcement' && new Date(a.sent_at || a.createdAt).getTime() > cutoff);
        setAnnCount(recent.length);
      })
      .catch(() => {});
  }, []);

  // Fetch unread message count (poll every 30s)
  React.useEffect(() => {
    const fetchMsgCount = () => {
      client.get('/student/messages')
        .then(({ data }) => setMsgCount(data?.unread_count || 0))
        .catch(() => {});
    };
    fetchMsgCount();
    const interval = setInterval(fetchMsgCount, 30000);
    return () => clearInterval(interval);
  }, []);

  // Decrement badge immediately when student reads a thread
  React.useEffect(() => {
    const handler = () => setMsgCount(prev => Math.max(0, prev - 1));
    window.addEventListener('student-msg-read', handler);
    return () => window.removeEventListener('student-msg-read', handler);
  }, []);

  // Clear badge when student opens the Announcements page
  React.useEffect(() => {
    if (location.pathname === '/student/announcements') {
      setAnnCount(0);
    }
  }, [location.pathname]);

  return (
    <div style={{ display: 'flex', height: '100vh', boxSizing: 'border-box', paddingTop: 'env(safe-area-inset-top)', background: COLORS.bg }}>
      {!isMobile && <Sidebar items={STUDENT_NAV} themeColor={color} badges={{ '/student/announcements': annCount, '/student/messages': msgCount }} />}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {isMobile && (
          <div style={{ background: color || '#7C3AED', paddingLeft: 16, paddingRight: 16, paddingTop: 10, paddingBottom: 10, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '60%' }}>{user?.full_name}</div>
            <span style={{ fontSize: 11, fontWeight: 700, background: 'rgba(255,255,255,0.22)', color: '#fff', borderRadius: 20, paddingTop: 3, paddingBottom: 3, paddingLeft: 10, paddingRight: 10, letterSpacing: 0.5, whiteSpace: 'nowrap' }}>Student</span>
          </div>
        )}
        <main style={{ flex: 1, overflow: 'auto', paddingBottom: isMobile ? 'calc(64px + env(safe-area-inset-bottom))' : 0, display: 'flex', flexDirection: 'column' }}>
          {children}
        </main>
      </div>
      {isMobile && <StudentBottomNav annCount={annCount} msgCount={msgCount} />}
    </div>
  );
}

function StudentLayout({ children }) {
  return (
    <SchoolBrandProvider>
      <StudentLayoutInner>{children}</StudentLayoutInner>
    </SchoolBrandProvider>
  );
}

/* ─── Owner Protected Route ─────────────────── */
function OwnerProtectedRoute({ children }) {
  const { user } = useAuth();
  const location = useLocation();
  const token = localStorage.getItem('token');
  if (!token || !user) return <Navigate to="/owner/login" replace />;
  if (user.role !== 'owner') return <Navigate to="/owner/login" replace />;
  if (!user.parent_school_id) return <Navigate to="/owner/login" replace />;
  // Force password change before accessing any owner page
  if (user.must_change_password && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }
  return children;
}

function ProtectedRoute({ children, allowedRoles }) {
  const { user, logout, updateUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem('token');

  if (!token || !user) return <Navigate to="/login" replace />;
  // Cashier shares the admin panel — treat as 'admin' for route access.
  // Registrar has its own portal at /registrar/* so keep its role as-is.
  const effectiveRole = user.role === 'cashier' ? 'admin' : user.role;
  if (allowedRoles && !allowedRoles.includes(effectiveRole)) return <Navigate to="/login" replace />;

  // If user must change password, redirect to change-password page (unless already there or impersonating)
  if (user.must_change_password && !user.impersonated_by && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace />;
  }

  // If user must reset their PIN (parent/student), redirect to change-pin page (unless already there or impersonating)
  if (user.must_reset_pin && !user.impersonated_by && location.pathname !== '/change-pin') {
    return <Navigate to="/change-pin" replace />;
  }

  // Terms gate — show modal if user hasn't accepted the current version
  // Skip for superadmin and SA-impersonated sessions
  const isImpersonatedSession = user.impersonated_by === 'superadmin';
  const needsTerms = user.role !== 'superadmin' && !isImpersonatedSession &&
    (!user.terms_accepted_version || user.terms_accepted_version !== CURRENT_TERMS_VERSION);

  if (needsTerms) {
    return (
      <>
        {/* Render the page behind the modal so layout is ready */}
        <div style={{ filter: 'blur(4px)', pointerEvents: 'none', userSelect: 'none' }}>
          {children}
        </div>
        <TermsModal
          onAccepted={() => {
            // Update local user state so the modal disappears without re-login
            updateUser({ ...user, terms_accepted_version: CURRENT_TERMS_VERSION });
          }}
          onLogout={() => {
            logout();
            navigate('/login', { replace: true });
          }}
        />
      </>
    );
  }

  return children;
}

function RoleRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (user.role === 'superadmin')    return <Navigate to="/sa"                   replace />;
  if (user.role === 'admin')         return <Navigate to="/admin"                replace />;
  if (user.role === 'teacher')   return <Navigate to="/teacher"  replace />;
  if (user.role === 'student')   return <Navigate to="/student"  replace />;
  if (user.role === 'parent')        return <Navigate to="/parent"               replace />;
  if (user.role === 'cashier')       return <Navigate to="/admin"                replace />;
  if (user.role === 'registrar')     return <Navigate to="/registrar/dashboard"  replace />;
  if (user.role === 'owner')         return <Navigate to="/owner/dashboard"      replace />;
  return <Navigate to="/login" replace />;
}

// Root "/" — authenticated users go to their portal; unauthenticated see the public homepage.
// On native iOS/Android, skip marketing and go straight to login.
function PublicRootRoute() {
  const { user } = useAuth();
  const token = localStorage.getItem('token');
  if (token && user) return <RoleRedirect />;
  if (isNativeApp) return <Navigate to="/login" replace />;
  return <PublicLayout><PublicHome /></PublicLayout>;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PermissionProvider>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            {/* ── Public marketing routes ──
                On native iOS/Android, all marketing pages redirect to login
                to comply with App Store guideline 3.1.1 (no org registration). */}
            <Route path="/"              element={<PublicRootRoute />} />
            <Route path="/features"      element={isNativeApp ? <Navigate to="/login" replace /> : <PublicLayout><PublicFeatures    /></PublicLayout>} />
            <Route path="/pricing"       element={isNativeApp ? <Navigate to="/login" replace /> : <PublicLayout><PublicPricing     /></PublicLayout>} />
            <Route path="/about"         element={isNativeApp ? <Navigate to="/login" replace /> : <PublicLayout><PublicAbout       /></PublicLayout>} />
            <Route path="/contact"       element={isNativeApp ? <Navigate to="/login" replace /> : <PublicLayout><PublicContact     /></PublicLayout>} />
            <Route path="/request-demo"  element={isNativeApp ? <Navigate to="/login" replace /> : <PublicLayout><PublicRequestDemo /></PublicLayout>} />
            <Route path="/privacy-policy" element={<PublicLayout><PublicPrivacyPolicy /></PublicLayout>} />

            {/* ── Auth routes ── */}
            <Route path="/login" element={<LoginGateway />} />
            <Route path="/login/staff" element={<Login />} />
            <Route path="/parent-login" element={<ParentLogin />} />
            <Route path="/student/login" element={<StudentLogin />} />

            {/* Admin routes
                PermGate is placed INSIDE AdminLayout so the sidebar remains visible
                even when a non-admin role doesn't have the required permission.
                Admin / superadmin always bypass PermGate.                         */}
            <Route path="/admin"                    element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminDashboard  /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/classes"            element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="manage_classes"><AdminClasses    /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/classes/:classId"              element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="manage_classes"><AdminClassDetail/></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/classes/:classId/timetable"    element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="manage_classes"><TimetableBuilder/></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/students"           element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate anyPerm={['manage_students','view_academic_records']}><AdminStudents   /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/teachers"           element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="manage_teachers"><AdminTeachers   /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/subjects"            element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="manage_classes"><AdminSubjects   /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/parents"                element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate anyPerm={['manage_parent_links','view_academic_records']}><AdminParents      /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/parents/:id"            element={<ProtectedRoute allowedRoles={['admin', 'registrar']}><RoleAwareAdminLayout><PermGate anyPerm={['manage_parent_links','view_academic_records']}><AdminParentDetail /></PermGate></RoleAwareAdminLayout></ProtectedRoute>} />
            <Route path="/admin/parents/:parentId/slip" element={<ProtectedRoute allowedRoles={['admin', 'registrar']}><AdminParentSlip /></ProtectedRoute>} />
            <Route path="/admin/fees"               element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="view_fees"><AdminFees       /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/fees/types"         element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="view_fees"><AdminFeeTypes   /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/fees/gateway"       element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="manage_settings"><GatewaySettings /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/reports"            element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="view_all_reports"><AdminReports       /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/announcements"     element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="send_announcements"><AdminAnnouncements /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/guide-center"      element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminGuideCenter   /></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/payroll"            element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="full_access"><AdminPayroll       /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/messages"           element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><AdminMessages      /></AdminLayout></ProtectedRoute>} />

            {/* Settings routes */}
            <Route path="/admin/settings/school-profile" element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="manage_settings"><SettingsSchoolProfile /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/settings/academic-years" element={<Navigate to="/admin/academic-years" replace />} />
            <Route path="/admin/settings/terms"          element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="manage_settings"><SettingsTerms         /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/settings/users-roles"    element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="manage_settings"><SettingsUsersRoles    /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/settings/payroll"        element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="full_access"><SettingsPayroll /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/settings/academic"       element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="manage_settings"><SettingsAcademic      /></PermGate></AdminLayout></ProtectedRoute>} />

            {/* Academic module routes */}
            <Route path="/admin/academic-years"          element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="manage_settings"><AcademicYearsPage          /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/academic/promotion"      element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="full_access"><PromotionWizard            /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/academics/report-cards"  element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="view_all_reports"><ReportCardsPage            /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/academics/student-records" element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate anyPerm={['manage_students','view_academic_records']}><StudentAcademicRecordsPage /></PermGate></AdminLayout></ProtectedRoute>} />
            <Route path="/admin/students/:studentId/academic-profile" element={<ProtectedRoute allowedRoles={['admin', 'registrar']}><RoleAwareAdminLayout><PermGate anyPerm={['manage_students','view_academic_records']}><StudentAcademicProfilePage /></PermGate></RoleAwareAdminLayout></ProtectedRoute>} />
            <Route path="/admin/academics/grade-flags"   element={<ProtectedRoute allowedRoles={['admin']}><AdminLayout><PermGate perm="view_all_reports"><GradeFlagsPage             /></PermGate></AdminLayout></ProtectedRoute>} />

            {/* Public payment return page — no auth required */}
            <Route path="/pay/return" element={<PayReturn />} />

            {/* Teacher routes */}
            <Route path="/teacher" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><TeacherHome /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/attendance" element={<ProtectedRoute allowedRoles={['teacher','admin']}><TeacherLayout><AttendanceSelectClass /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/attendance/:classId" element={<ProtectedRoute allowedRoles={['teacher','admin']}><TeacherLayout><AttendanceMarking /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/grades" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><GradesSelectClass /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/grades/:classId" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><GradesTable /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/alerts" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><TeacherAlerts /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/profile" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><TeacherProfile /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/notes" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><ClassNotes /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/students" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><ViewStudents /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/students/:studentId" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><StudentDetail /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/report" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><ClassReport /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/timetable" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><MyTimetable /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/messages" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><TeacherMessages /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/homework" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><TeacherHomework /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/homework/create" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><CreateHomework /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/homework/:homeworkId/submissions" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><TeacherSubmissions /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/classes/:classId" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><ClassDashboard /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/notifications" element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><NotificationCenter /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/payslip"         element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><TeacherPayslip        /></TeacherLayout></ProtectedRoute>} />
            <Route path="/teacher/grade-requests"  element={<ProtectedRoute allowedRoles={['teacher']}><TeacherLayout><TeacherGradeRequests  /></TeacherLayout></ProtectedRoute>} />

            {/* Parent routes */}
            <Route path="/parent"              element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentHome /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/attendance"   element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentAttendance /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/grades"       element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentGrades /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/fees"         element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentFees /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/homework"     element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentHomework /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/messages"     element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentMessages /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/timetable"       element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentTimetable /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/profile"         element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentProfile /></ParentLayout></ProtectedRoute>} />
            <Route path="/parent/announcements"   element={<ProtectedRoute allowedRoles={['parent']}><ParentLayout><ParentAnnouncements /></ParentLayout></ProtectedRoute>} />

            {/* Student routes */}
            <Route path="/student" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentHome /></StudentLayout></ProtectedRoute>} />
            <Route path="/student/homework" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentHomework /></StudentLayout></ProtectedRoute>} />
            <Route path="/student/grades" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentGrades /></StudentLayout></ProtectedRoute>} />
            <Route path="/student/attendance" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentAttendance /></StudentLayout></ProtectedRoute>} />
            <Route path="/student/timetable" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentTimetable /></StudentLayout></ProtectedRoute>} />
            <Route path="/student/announcements" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentAnnouncements /></StudentLayout></ProtectedRoute>} />
            <Route path="/student/messages" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentMessages /></StudentLayout></ProtectedRoute>} />
            <Route path="/student/profile" element={<ProtectedRoute allowedRoles={['student']}><StudentLayout><StudentProfile /></StudentLayout></ProtectedRoute>} />

            {/* Admin — Student Slip (printable, no sidebar) — also accessible by registrar */}
            <Route path="/admin/students/:studentId/slip" element={<ProtectedRoute allowedRoles={['admin', 'registrar']}><AdminStudentSlip /></ProtectedRoute>} />
            {/* Admin — Teacher Slip (printable, no sidebar) */}
            <Route path="/admin/teachers/:teacherId/slip" element={<ProtectedRoute allowedRoles={['admin']}><AdminTeacherSlip /></ProtectedRoute>} />
            {/* Admin — Staff Login Slip (registrar, cashier, etc.) */}
            <Route path="/admin/users/:userId/slip" element={<ProtectedRoute allowedRoles={['admin']}><AdminStaffSlip /></ProtectedRoute>} />

            {/* ── Registrar Portal ── */}
            <Route path="/registrar/dashboard" element={<ProtectedRoute allowedRoles={['registrar']}><RegistrarLayout><RegistrarDashboard /></RegistrarLayout></ProtectedRoute>} />
            <Route path="/registrar/students"   element={<ProtectedRoute allowedRoles={['registrar']}><RegistrarLayout><AdminStudents     /></RegistrarLayout></ProtectedRoute>} />
            <Route path="/registrar/enrollment" element={<ProtectedRoute allowedRoles={['registrar']}><RegistrarLayout><AdminStudents     /></RegistrarLayout></ProtectedRoute>} />
            <Route path="/registrar/transfers"  element={<ProtectedRoute allowedRoles={['registrar']}><RegistrarLayout><AdminStudents     /></RegistrarLayout></ProtectedRoute>} />
            <Route path="/registrar/parents"    element={<ProtectedRoute allowedRoles={['registrar']}><RegistrarLayout><AdminParents      /></RegistrarLayout></ProtectedRoute>} />
            <Route path="/registrar/academics"  element={<ProtectedRoute allowedRoles={['registrar']}><RegistrarLayout><StudentAcademicRecordsPage /></RegistrarLayout></ProtectedRoute>} />
            <Route path="/registrar/reports"    element={<ProtectedRoute allowedRoles={['registrar']}><RegistrarLayout><RegistrarReports  /></RegistrarLayout></ProtectedRoute>} />
            <Route path="/registrar/messages"   element={<ProtectedRoute allowedRoles={['registrar']}><RegistrarLayout><AdminMessages     /></RegistrarLayout></ProtectedRoute>} />
            <Route path="/registrar/profile"    element={<ProtectedRoute allowedRoles={['registrar']}><RegistrarLayout><RegistrarProfile  /></RegistrarLayout></ProtectedRoute>} />

            <Route path="/change-password" element={<ProtectedRoute><ChangePassword /></ProtectedRoute>} />
            <Route path="/change-pin"      element={<ProtectedRoute allowedRoles={['parent','student']}><ChangePIN /></ProtectedRoute>} />

            {/* Super Admin Portal — no auth wrapper, handles its own auth */}
            <Route path="/sa/*" element={<SuperAdminPortal />} />

            {/* ── Owner Portal ── */}
            <Route path="/owner/login"               element={<OwnerLogin />} />
            <Route path="/owner/dashboard"           element={<OwnerProtectedRoute><OwnerDashboard    /></OwnerProtectedRoute>} />
            <Route path="/owner/branches/:branchId"  element={<OwnerProtectedRoute><OwnerBranchDetail /></OwnerProtectedRoute>} />

            <Route path="*" element={<RoleRedirect />} />
          </Routes>
        </Suspense>
        </PermissionProvider>
      </BrowserRouter>
    </AuthProvider>
  );
}
